import {
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { directionForLocale, pickBilingual, type Locale } from '@lims/shared-types';
import { useQuery } from '@tanstack/react-query';
import { resolveApiPath } from '../api/client.js';
import { dashboardApi, settingsApi, type DashboardSummary } from '../api/endpoints.js';
import { Breadcrumbs, TranslatedCloseButton, type Crumb } from '../components/ui/index.js';
import { IconFlask } from '../components/ui/nav-icons.js';
import { POPOVER_MOTION_CLASS } from '../components/ui/popover-motion.js';
import { ShellCrumbsProvider, useShellCrumbs } from '../components/ui/shell-slot.js';
import { changeLanguage, currentLocale } from '../i18n/index.js';
import { NotificationsBell } from '../modules/notifications/NotificationsBell.js';
import { useAuth } from './auth-context.js';
import { activeEntry, NAV_GROUPS, type NavEntry, type NavGroup } from './navigation.js';

/**
 * The authenticated application shell (D278, the approved "Calm sage"
 * redesign, prototype 3C).
 *
 *  - ONE light sidebar on the leading edge. The destinations are grouped into
 *    six modules (`navigation.ts`); one group is open at a time, and the group
 *    holding the current page opens by itself. The sidebar collapses to its
 *    icons, and remembers that choice on this device.
 *  - A top bar over the page: where you are (breadcrumbs), the language, the
 *    notifications bell and the account menu.
 *  - Below `lg` the sidebar becomes a drawer behind a menu button, carrying the
 *    identical navigation. The drawer closes on navigation.
 *
 * Navigation entries are filtered by the caller's permissions so people are not
 * shown doors they cannot open. That filtering is a COURTESY: every route
 * behind these links calls an endpoint that re-checks the same permission on
 * the server (CLAUDE.md section 5.1).
 *
 * DIRECTION. Nothing here says `left` or `right` except the drawer's placement,
 * which HeroUI only accepts as a physical side and which is therefore derived
 * from the resolved direction. Everything else is a logical property, so the
 * whole frame mirrors under Arabic.
 */

const COLLAPSE_KEY = 'lims.sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0');
  } catch {
    // Storage can be unavailable (private window, blocked site data); the
    // sidebar then simply forgets the choice on the next visit.
  }
}

export function AppShell() {
  return (
    <ShellCrumbsProvider>
      <ShellFrame />
    </ShellCrumbsProvider>
  );
}

function ShellFrame() {
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const location = useLocation();
  const locale = currentLocale();
  const direction = directionForLocale(locale);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isCollapsed, setCollapsed] = useState(readCollapsed);

  // Close the drawer whenever the route changes, however the change happened.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const laboratoryName = user?.tenant
    ? pickBilingual({ ar: user.tenant.nameAr, en: user.tenant.nameEn }, locale)
    : null;

  /**
   * The laboratory's own logo, from its settings — fetched only when the caller
   * may read settings, and drawn only when the laboratory has set one. There is
   * deliberately NO fallback image bundled with the product: a shipped logo
   * would assert an identity that belongs to a customer (CLAUDE.md section
   * 6.1). Without one, the product's own flask mark stands in; it marks the
   * SOFTWARE and is never presented as the laboratory's.
   */
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
  });
  const logo = settings.data?.logo ?? null;

  /**
   * The live counts beside Samples, Lab work, Review and the rest: the same
   * dashboard summary the home page reads (shared cache). A section the server
   * returns as `null` is one this person may not see, and its count is simply
   * absent — never drawn as a zero.
   */
  const summary = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.summary(),
    enabled: can(PERMISSIONS.DASHBOARD_VIEW),
    refetchInterval: 120_000,
  });

  const groups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        entries: group.entries.filter((entry) => {
          // `import.meta.env.DEV` is a compile-time constant, so a production
          // build drops this entry rather than hiding it in the shipped bundle.
          if (entry.isDevelopmentOnly && !import.meta.env.DEV) return false;
          if (entry.anyPermission) return entry.anyPermission.some((key) => can(key));
          return !entry.permission || can(entry.permission);
        }),
      })).filter((group) => group.entries.length > 0),
    [can],
  );

  const current = activeEntry(groups, location.pathname);

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      writeCollapsed(!value);
      return !value;
    });
  };

  const identity = (compact: boolean) => (
    <Link
      to="/"
      className={`flex min-w-0 items-center gap-2.5 rounded-medium no-underline ${compact ? 'justify-center' : ''}`}
      aria-label={laboratoryName ?? t('common.appNameShort')}
    >
      {logo ? (
        <img
          src={resolveApiPath(logo.url)}
          alt=""
          className="size-9 shrink-0 rounded-medium object-contain"
        />
      ) : (
        <span className="grid size-9 shrink-0 place-items-center rounded-medium bg-primary-600 text-white">
          <IconFlask size={19} />
        </span>
      )}
      {compact ? null : (
        <span className="min-w-0 text-[13px] font-bold leading-tight text-default-900">
          {laboratoryName ?? t('common.appNameShort')}
        </span>
      )}
    </Link>
  );

  return (
    <div
      className={`min-h-screen bg-background lg:grid ${
        isCollapsed ? 'lg:grid-cols-[64px_minmax(0,1fr)]' : 'lg:grid-cols-[252px_minmax(0,1fr)]'
      }`}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded-medium focus:bg-white focus:px-4 focus:py-2 focus:shadow-medium"
      >
        {t('common.skipToContent')}
      </a>

      {/* The sidebar. Sticky, so navigation stays put while a register scrolls. */}
      <aside className="sticky top-0 hidden h-screen flex-col border-e border-default-200 bg-side lg:flex">
        <div className={`flex h-14 shrink-0 items-center border-b border-default-200 ${isCollapsed ? 'justify-center px-2' : 'px-4'}`}>
          {identity(isCollapsed)}
        </div>
        <h2 id="side-nav" className="sr-only">
          {t('common.mainNavigation')}
        </h2>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
          <SideNav
            groups={groups}
            current={current}
            summary={summary.data}
            headingId="side-nav"
            isCollapsed={isCollapsed}
          />
        </div>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-pressed={isCollapsed}
          aria-label={isCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          title={isCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          className={`flex h-11 shrink-0 items-center gap-2 border-t border-default-200 text-caption text-default-600 transition-colors hover:bg-side-hover hover:text-default-900 ${
            isCollapsed ? 'justify-center' : 'px-4'
          }`}
        >
          <SidebarIcon />
          {isCollapsed ? null : <span>{t('nav.collapseSidebar')}</span>}
        </button>
      </aside>

      {/*
        Below `lg`, the same navigation inside a drawer.

        HeroUI's `placement` is a PHYSICAL side, not a logical one: it never
        flips for Arabic on its own, so the placement is derived from the
        resolved direction and the drawer opens from the leading edge in both
        languages. Its close button is `TranslatedCloseButton`, named in the
        reader's language rather than HeroUI's fixed English "Close".
      */}
      <Drawer
        isOpen={isDrawerOpen}
        onOpenChange={setDrawerOpen}
        placement={direction === 'rtl' ? 'right' : 'left'}
        size="xs"
        classNames={{ base: 'bg-side' }}
        closeButton={<TranslatedCloseButton />}
      >
        <DrawerContent>
          <div className="flex h-14 shrink-0 items-center border-b border-default-200 px-4 pe-12">
            {identity(false)}
          </div>
          <DrawerBody className="px-2 py-3">
            <h2 id="drawer-nav" className="sr-only">
              {t('common.mainNavigation')}
            </h2>
            <SideNav
              groups={groups}
              current={current}
              summary={summary.data}
              headingId="drawer-nav"
              isCollapsed={false}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <div className="flex min-w-0 flex-col">
        <TopBar
          current={current}
          onOpenMenu={() => setDrawerOpen(true)}
        />
        <main id="main" className="min-w-0 flex-1 px-4 py-5 sm:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The sidebar navigation
// ---------------------------------------------------------------------------

function SideNav({
  groups,
  current,
  summary,
  headingId,
  isCollapsed,
}: {
  readonly groups: readonly NavGroup[];
  readonly current: { group: NavGroup; entry: NavEntry } | null;
  readonly summary: DashboardSummary | undefined;
  readonly headingId: string;
  readonly isCollapsed: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentGroupId = current?.group.id ?? null;
  // One group open at a time; the group holding the current page opens itself
  // whenever the page changes group.
  const [openId, setOpenId] = useState<string | null>(currentGroupId);
  useEffect(() => {
    if (currentGroupId) setOpenId(currentGroupId);
  }, [currentGroupId]);

  const countOf = (entry: NavEntry): number | null => {
    if (!summary || !entry.count) return null;
    const value = entry.count(summary);
    return typeof value === 'number' ? value : null;
  };
  const groupCount = (group: NavGroup): number =>
    group.entries.reduce((sum, entry) => sum + (countOf(entry) ?? 0), 0);

  const main = groups.filter((group) => !group.isFoot);
  const foot = groups.filter((group) => group.isFoot);

  const renderGroup = (group: NavGroup) => {
    const isOpen = openId === group.id;
    const isCurrent = currentGroupId === group.id;
    const total = groupCount(group);
    const panelId = `${headingId}-${group.id}`;

    if (isCollapsed) {
      // Icons only: a group's icon goes straight to its first destination.
      const first = group.entries[0];
      return (
        <li key={group.id}>
          <button
            type="button"
            onClick={() => first && navigate(first.to)}
            title={t(group.labelKey)}
            aria-label={t(group.labelKey)}
            {...(isCurrent ? { 'aria-current': 'true' as const } : {})}
            className={`relative mx-auto grid size-10 place-items-center rounded-medium transition-colors ${
              isCurrent ? 'bg-side-on text-side-on-ink' : 'text-default-700 hover:bg-side-hover hover:text-default-900'
            }`}
          >
            <group.Icon size={19} />
            {total > 0 ? (
              <span className="absolute end-1 top-1 size-2 rounded-full bg-primary-600" aria-hidden="true" />
            ) : null}
          </button>
        </li>
      );
    }

    return (
      <li key={group.id}>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpenId(isOpen ? null : group.id)}
          className={`flex w-full items-center gap-2.5 rounded-medium px-2.5 py-2 text-start text-body font-semibold transition-colors hover:bg-side-hover ${
            isCurrent ? 'text-default-900' : 'text-default-800'
          }`}
        >
          <group.Icon size={18} />
          <span className="min-w-0 flex-1 truncate">{t(group.labelKey)}</span>
          {total > 0 && !isOpen ? <CountPill value={total} /> : null}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
            className={`shrink-0 text-default-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <ul id={panelId} hidden={!isOpen} className="mb-1.5 mt-0.5 flex flex-col gap-px">
          {group.entries.map((entry) => {
            const isActive = current?.entry.to === entry.to;
            const count = countOf(entry);
            return (
              <li key={entry.to}>
                <Link
                  to={entry.to}
                  {...(isActive ? { 'aria-current': 'page' as const } : {})}
                  className={`flex items-center gap-2 rounded-medium py-1.5 pe-2.5 ps-[38px] text-body no-underline transition-colors ${
                    isActive
                      ? 'bg-side-on font-semibold text-side-on-ink'
                      : 'text-default-700 hover:bg-side-hover hover:text-default-900'
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{t(entry.labelKey)}</span>
                  {count !== null && count > 0 ? <CountPill value={count} /> : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </li>
    );
  };

  return (
    <nav aria-labelledby={headingId} className="flex h-full flex-col">
      <ul className="flex flex-col gap-0.5">{main.map(renderGroup)}</ul>
      {foot.length > 0 ? (
        <ul className="mt-auto flex flex-col gap-0.5 border-t border-default-200 pt-2">{foot.map(renderGroup)}</ul>
      ) : null}
    </nav>
  );
}

function CountPill({ value }: { readonly value: number }) {
  return (
    <span className="min-w-[22px] shrink-0 rounded-full bg-primary-100 px-1.5 text-center text-micro font-semibold tabular-nums leading-5 text-primary-800">
      {value > 999 ? '999+' : value}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The top bar
// ---------------------------------------------------------------------------

function TopBar({
  current,
  onOpenMenu,
}: {
  readonly current: { group: NavGroup; entry: NavEntry } | null;
  readonly onOpenMenu: () => void;
}) {
  const { t } = useTranslation();
  const published = useShellCrumbs();

  /*
    A page that declares its own breadcrumbs owns the trail. One that does not
    still says where it is: its module, then its destination.
  */
  const fallback: readonly Crumb[] = current
    ? [{ label: t(current.group.labelKey) }, { label: t(current.entry.labelKey) }]
    : [];
  const crumbs = published && published.length > 0 ? published : fallback;

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-default-200 bg-white px-3 sm:px-5">
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={t('common.openMenu')}
        className="grid size-9 shrink-0 place-items-center rounded-medium text-default-700 hover:bg-default-100 lg:hidden"
      >
        <MenuIcon />
      </button>
      <div className="min-w-0 flex-1 overflow-hidden">
        <Breadcrumbs items={crumbs} />
      </div>
      <LanguageToggle />
      <NotificationsBell />
      <AccountMenu />
    </header>
  );
}

/**
 * The language toggle: one button naming the OTHER language, in that language
 * (an endonym), so a reader who cannot read the current one still finds their
 * own. It carries the `lang` of the language it names, so a screen reader
 * pronounces it correctly.
 */
function LanguageToggle() {
  const { t } = useTranslation();
  const active = currentLocale();
  const other: Locale = active === 'ar' ? 'en' : 'ar';
  const name = other === 'ar' ? t('common.arabic') : t('common.english');
  return (
    <button
      type="button"
      lang={other}
      onClick={() => void changeLanguage(other)}
      aria-label={name}
      title={name}
      className="h-9 shrink-0 rounded-medium px-2.5 text-caption font-semibold text-default-700 hover:bg-default-100 hover:text-default-900"
    >
      {other === 'ar' ? 'ع' : 'EN'}
    </button>
  );
}

function AccountMenu() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const name = user?.displayName ?? '';
  const initials = name
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    // No HeroUI popover animation: its last frame flashed (see SelectField).
    // The shared CSS motion replaces it (`popover-motion.ts`, D196).
    <Dropdown placement="bottom-end" disableAnimation classNames={{ content: POPOVER_MOTION_CLASS }}>
      <DropdownTrigger>
        <button
          type="button"
          aria-label={t('common.accountMenu')}
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-medium ps-1 pe-1.5 hover:bg-default-100"
        >
          <span className="grid size-7 place-items-center rounded-full bg-primary-100 text-micro font-bold text-primary-800">
            {initials || '·'}
          </span>
          <span className="hidden max-w-36 truncate text-caption font-medium text-default-800 xl:block">{name}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" className="text-default-600">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </DropdownTrigger>
      <DropdownMenu aria-label={t('common.accountMenu')}>
        <DropdownItem key="identity" isReadOnly className="opacity-100" textValue={user?.email ?? ''}>
          <span className="block text-body font-semibold">{name}</span>
          <span className="block text-caption text-default-600">{user?.email}</span>
          <Chip size="sm" variant="flat" radius="sm" className="mt-1">
            {t(`roles.${user?.role ?? 'technician'}`)}
          </Chip>
        </DropdownItem>
        <DropdownItem key="profile" onPress={() => navigate('/profile')}>
          {t('profile.title')}
        </DropdownItem>
        <DropdownItem key="notification-settings" onPress={() => navigate('/notifications/settings')}>
          {t('notifications.settingsLink')}
        </DropdownItem>
        <DropdownItem key="signout" color="danger" onPress={() => void handleSignOut()}>
          {t('auth.signOut')}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function SidebarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </svg>
  );
}
